<script lang="ts" setup>
import { ViewBlockDetailInfo } from '~/api/service/block/[blockNumber]/index.service'
import { useResponsiveScreen } from '~/composables/useResponsiveScreen'
import { UNIT } from '~/environment-variables/unit.const'
import BcLabelContainer from '~/ui/LabelContainer/BcLabelContainer.vue'
import BcLink from '~/ui/Link/BcLink.vue'

interface IProps {
  data: ViewBlockDetailInfo
}

defineProps<IProps>()

const { isMobile } = useResponsiveScreen()
const route = useRoute()

const isBlockDetailPage = computed(() => {
  return route.path.includes('blocks')
})
</script>

<template>
  <div
    v-if="data"
    class="block-container"
  >
    <div class="bc-detail-left">
      <div class="block-number-container">
        <div class="w-32">
          <span class="bc-label">Block Number</span>
        </div>
        <span
          v-if="isBlockDetailPage"
          class="text-lg/[1.25rem]"
        >{{ data.BNO }}</span>
        <BcLink
          v-else
          class="text-lg/[1.25rem]"
          :content="data.BNO"
          :to="routes.getBlockDetailPageWithTab(data.BNO)"
        />
      </div>
      <div class="block-time-container">
        <div class="bc-label w-32">
          <span class="bc-label">Timestamp</span>
        </div>
        <div class="flex flex-col">
          <span class="text-lg/[1.25rem] text-primary">{{ data.AG }}</span>
          <time class="bc-label">({{ data.unixTimeStamp }})</time>
          <div class="flex flex-wrap gap-x-4">
            <div class="txn-cnt-container">
              <FontAwesomeIcon
                class="text-xl text-explorer-blue"
                icon="external-link-square-alt"
              />
              <BcLink
                tooltip="click to view transaction"
                :content="`${data.TCNTINFO.TRXCNT} txns`"
                :replace="true"
                :to="routes.getBlockDetailPageWithTab(data.BNO, blockDetailPageType.transactions)"
                class="txn-link"
              />
            </div>
            <div class="txn-cnt-container">
              <FontAwesomeIcon
                class="text-xl text-explorer-blue"
                icon="external-link-square-alt"
              />
              <BcLink
                tooltip="click to view internal transaction"
                :content="`${data.TCNTINFO.CINCNT} internal txns`"
                :replace="true"
                :to="routes.getBlockDetailPageWithTab(data.BNO, blockDetailPageType.internalTransactions)"
                class="txn-link"
              />
            </div>
          </div>
        </div>
      </div>
      <BcLabelContainer
        class="block-reward-container"
        label="Block Reward"
      >
        <template #content>
          <div class="mt-1 gap-1">
            <span class="text-lg/[1.25rem]">{{ data.blockReward }}</span> <span
              class="text-xl/[1.5rem] text-primary/40"
            >{{ UNIT }}</span>
          </div>
        </template>
      </BcLabelContainer>
      <BcLabelContainer
        class="mt-4"
        label="Hash"
      >
        <template #content>
          <span class="bc-hash">{{ data.BHA }}</span>
        </template>
      </BcLabelContainer>
      <BcLabelContainer
        class="mt-4"
        label="Parent Hash"
      >
        <template #content>
          <BcLink
            class="bc-hash"
            tooltip="click to view block"
            :content="data.PRHA"
            :to="routes.getBlockDetailPageWithTab((parseInt(data.BNO) - 1).toString(), blockDetailPageType.block)"
            :line-break="!!isMobile"
          />
        </template>
      </BcLabelContainer>
      <BcLabelContainer
        class="mt-4"
        label="Validator"
      >
        <template #content>
          <!--            <AddressCopyLink-->
          <!--              class="bc-hash"-->
          <!--              tooltip="click to view address"-->
          <!--              :address="data.VD"-->
          <!--              type="EOA"-->
          <!--              hide-eoa-icon-->
          <!--              is-detail-->
          <!--              :line-break="!!isMobile"-->
          <!--            />-->
          <span class="bc-hash">
            {{ data.VD }}
          </span>
        </template>
      </BcLabelContainer>
    </div>

    <div class="bc-divider" />

    <div class="bc-detail-right">
      <div class="bc-label-container">
        <span class="bc-label w-40">Gas Used</span>
        <div class="flex">
          <span class="text-lg/[1.25rem]">{{ data.gasUsed }}</span>
          <Chip
            class="ml-2"
            :value="`${data.GUP} %`"
          />
        </div>
      </div>
      <div class="bc-label-container">
        <span class="bc-label w-40">Gas Limit</span>
        <span class="text-lg/[1.25rem]">{{ data.gasLimit }}</span>
      </div>
      <div class="bc-label-container">
        <span class="bc-label w-40">Base Fee Per Gas</span>
        <div class="flex flex-wrap gap-x-1">
          <span class="text-lg/[1.25rem]">{{ data.baseFeePerGas }}</span>
          <span class="bc-label">{{ UNIT }}</span>
          <span class="bc-label">({{ data.baseFeePerGasGwei }} Gwei)</span>
        </div>
      </div>
      <div class="bc-label-container">
        <span class="bc-label w-40">Gas Fees</span>
        <div class="flex">
          <span class="text-lg/[1.25rem]">{{ data.burntFees }}</span>
          <span class="bc-label ml-1">{{ UNIT }}</span>
        </div>
      </div>
      <div class="bc-label-container">
        <span class="bc-label w-40">Difficulty</span>
        <span class="text-lg/[1.25rem]">{{ data.DF }}</span>
      </div>
      <div class="bc-label-container">
        <span class="bc-label w-40">Total Difficulty</span>
        <span class="text-lg/[1.25rem]">{{ data.totalDifficulty }}</span>
      </div>
      <div class="bc-label-container">
        <span class="bc-label w-40">Size</span>
        <div class="flex">
          <span class="text-lg/[1.25rem]">{{ data.size }}</span>
          <span class="bc-label ml-1">bytes</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.txn-cnt-container {
  @apply flex items-center gap-2 p-2;
}

.bc-label {
  @apply text-lg/[1.25rem] text-primary/40 shrink-0;
}

.txn-link {
  @apply text-lg/[1.25rem];
}

.bc-hash {
  @apply mt-1 truncate text-lg/[1.25rem];

  @include sm {
    @apply whitespace-normal break-all;
  }
}

.bc-divider {
  @apply border-l border-primary/10;

  @include sm {
    @apply border-l-0 border-b;
  }
}

.block-container {
  @apply p-8 text-primary leading-none grid gap-4 font-bold;
  grid-template-columns: 1fr auto 1fr;

  @include sm {
    @apply grid-cols-1;
  }

  .bc-detail-left {
    @apply flex flex-col truncate;
    .block-number-container {
      @apply flex;

      @include sm {
        @apply flex-col gap-1;
      }
    }

    .block-time-container {
      @apply mt-4 flex;

      @include sm {
        @apply flex-col gap-1;
      }
    }

    .block-reward-container {
      @apply mt-8;

      @include sm {
        @apply mt-4;
      }
    }
  }

  .bc-detail-right {
    @apply flex flex-col gap-8;

    @include sm {
      @apply gap-4;
    }

    .bc-label-container {
      @apply flex;

      @include sm {
        @apply flex-col gap-1;
      }
    }
  }
}

</style>